var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Frustum, LinearFilter, PerspectiveCamera, RGBAFormat, Vector4, WebGLRenderTarget } from "three";
import { AskErrorMessage } from "flavr-common";
import { RequestCameraTakeSnapshotMessage, ResponseCameraTakeSnapshotMessage } from "@messages/media";
import { animationFrame$ } from "@messages/xr/AnimationFrame";
import { now } from "@messages/timing";
import { mediaFileService } from "@modules/media";
import { renderManager } from "@modules/xr/RenderManager";
import { localPlayerContainer } from "@modules/localplayer/LocalPlayerContainer";
import { CameraRecorderViewRenderable } from "./CameraRecorderViewRenderable";
import { pixelsToPNG } from "./ImageUtils";
import { mainScene } from "../mainScene";
import { renderer } from "../RendererGlobals";
import { MeshRenderable } from "../mesh/MeshRenderable";
import { isRenderableType } from "../renderable/Renderable";
import { Layer } from "../RenderOrder";
import { capabilitiesService } from "../CapabilitiesService";
export const CAMERA_RENDER_WIDTH = 320;
export const CAMERA_RENDER_HEIGHT = 240;
export const CAMERA_SNAPSHOT_RENDER_WIDTH = 1024;
export const CAMERA_SNAPSHOT_RENDER_HEIGHT = 768;
const MAX_RENDER_DISTANCE_SQ = 10 * 10;
export class CameraViewSystem {
    constructor(rendererModule) {
        this.rendererModule = rendererModule;
        this.camera = new PerspectiveCamera(50, CAMERA_RENDER_WIDTH / CAMERA_RENDER_HEIGHT, 0.1, 100);
        this.savedViewport = new Vector4();
        this.savedScissor = new Vector4();
        this.snapshotRenderTarget = new WebGLRenderTarget(CAMERA_SNAPSHOT_RENDER_WIDTH, CAMERA_SNAPSHOT_RENDER_HEIGHT, {
            minFilter: LinearFilter,
            magFilter: LinearFilter,
            format: RGBAFormat
        });
        this.views = new Set();
        this.lastRenderTime = 0;
        this.renderInterval = 100;
        /**
         * On constrained platforms (mobile VR/mobile/tablet) an extra full scene render pass every renderInterval is too
         * costly to do at the same rate as desktop. Render far less often instead of never - a slowly refreshing live
         * view is both cheap enough for these platforms and communicates that the screen is actually live, rather than
         * leaving it permanently black.
         */
        this.reducedRenderInterval = 1000;
        this.frustum = new Frustum();
        this.update = () => {
            const time = now();
            const isConstrainedPlatform = capabilitiesService.isMobileVR || capabilitiesService.isMobile() || capabilitiesService.isTablet;
            const interval = isConstrainedPlatform ? this.reducedRenderInterval : this.renderInterval;
            if (time - this.lastRenderTime < interval)
                return;
            this.lastRenderTime = time;
            this.frustum.setFromProjectionMatrix(renderManager.currentProjectionScreenMatrix);
            for (const renderable of this.views.values()) {
                if (renderable.facet === undefined)
                    continue;
                if (renderable.disableRender)
                    continue;
                const force = renderable.takeSnapShotNextFrame !== undefined;
                if (!force) {
                    if (localPlayerContainer.cameraWorldPosition.distanceToSquared(renderable.owner.worldPosition) >= MAX_RENDER_DISTANCE_SQ) {
                        continue;
                    }
                    if (!renderable.checkFrustum(this.frustum))
                        continue;
                }
                const model = this.rendererModule.getModel(renderable.facet.cameraUid);
                if (model === undefined)
                    continue;
                let wasVisible = true;
                if (renderable.owner.renderable !== undefined && isRenderableType(MeshRenderable, renderable.owner.renderable)) {
                    wasVisible = renderable.owner.renderable.meshInstance.getVisible();
                    if (wasVisible) {
                        renderable.owner.renderable.meshInstance.setVisible(false);
                    }
                }
                let renderWidth = CAMERA_RENDER_WIDTH;
                let renderHeight = CAMERA_RENDER_HEIGHT;
                let renderTarget = renderable.renderTarget;
                if (renderable.takeSnapShotNextFrame !== undefined) {
                    renderWidth = CAMERA_SNAPSHOT_RENDER_WIDTH;
                    renderHeight = CAMERA_SNAPSHOT_RENDER_HEIGHT;
                    renderTarget = this.snapshotRenderTarget;
                }
                this.camera.position.copy(model.worldPosition);
                this.camera.quaternion.copy(model.worldOrientation);
                this.camera.updateMatrix();
                this.camera.updateMatrixWorld();
                const webXREnabled = renderer.xr.enabled;
                const autoUpdate = mainScene.matrixWorldAutoUpdate;
                const savedRenderTarget = renderer.getRenderTarget();
                const savedScissorTest = renderer.getScissorTest();
                renderer.getViewport(this.savedViewport);
                renderer.getScissor(this.savedScissor);
                renderer.xr.enabled = false;
                mainScene.matrixWorldAutoUpdate = false;
                // Render to camera render target
                renderer.setRenderTarget(renderTarget);
                renderer.setViewport(0, 0, renderWidth, renderHeight);
                renderer.setScissor(0, 0, renderWidth, renderHeight);
                renderer.setScissorTest(true);
                renderer.render(mainScene, this.camera);
                // Reset renderer state
                renderer.setRenderTarget(savedRenderTarget);
                renderer.setViewport(this.savedViewport);
                renderer.setScissor(this.savedScissor);
                renderer.setScissorTest(savedScissorTest);
                renderer.xr.enabled = webXREnabled;
                mainScene.matrixWorldAutoUpdate = autoUpdate;
                if (wasVisible && renderable.owner.renderable !== undefined && isRenderableType(MeshRenderable, renderable.owner.renderable)) {
                    renderable.owner.renderable.meshInstance.setVisible(true);
                }
                if (renderable.takeSnapShotNextFrame !== undefined) {
                    const askId = renderable.takeSnapShotNextFrame.askId;
                    renderable.takeSnapShotNextFrame = undefined;
                    void this.takeSnapshot(renderable, renderTarget, renderWidth, renderHeight, askId);
                }
            }
        };
        this.handleRequestCameraTakeSnapshotMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            try {
                const model = this.rendererModule.getModel(msg.uid);
                if (model !== undefined) {
                    const facet = model.getFacetRenderer(CameraRecorderViewRenderable);
                    if (facet !== undefined) {
                        facet.takeSnapShotNextFrame = { askId };
                        return;
                    }
                }
            }
            catch (error) {
                console.error(error);
            }
            if (askId !== undefined) {
                this.rendererModule
                    .getConnection()
                    .answer(askId, [
                    new AskErrorMessage(`CameraViewSystem.handleRequestCameraTakeSnapshotMessage: Snapshot can't be taken for the model with uid ${msg.uid}`)
                ]);
            }
        });
        this.camera.layers.enable(Layer.CAMERA_LAYER_VIDEO_TEXTURE_TARGET);
        this.camera.layers.enable(Layer.POSTPROCESSING_OVERLAY);
        const connection = this.rendererModule.getConnection();
        connection.subscribeByTypeOrAskRequest(RequestCameraTakeSnapshotMessage, this.handleRequestCameraTakeSnapshotMessage);
        animationFrame$.onPreRender$.add(this.update);
    }
    addView(view) {
        this.views.add(view);
    }
    removeView(view) {
        this.views.delete(view);
    }
    takeSnapshot(renderable, renderTarget, renderWidth, renderHeight, askId) {
        return __awaiter(this, void 0, void 0, function* () {
            const snapPixels = new Uint8Array(renderWidth * renderHeight * 4);
            renderer.readRenderTargetPixels(renderTarget, 0, 0, renderWidth, renderHeight, snapPixels);
            const result = yield pixelsToPNG("snap.png", snapPixels, renderWidth, renderHeight);
            if (result === undefined) {
                if (askId !== undefined) {
                    this.rendererModule.getConnection().answer(askId, [new AskErrorMessage("CameraViewSystem.takeSnapshot: pixelsToPNG failed")]);
                }
                return;
            }
            const fileId = mediaFileService.addMediaFile(result.url, { file: result.file });
            if (askId !== undefined) {
                this.rendererModule.getConnection().answer(askId, [new ResponseCameraTakeSnapshotMessage(renderable.owner.modelUID, fileId, result.url)]);
            }
        });
    }
}
